import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Info, TrendingUp, Zap } from 'lucide-react';

const MedicationIntelligencePanel = ({ insights = [] }) => {
  if (!insights || insights.length === 0) {
    return null;
  }

  const getSeverityConfig = (severity) => {
    const configs = {
      high: {
        bgClass: 'bg-red-50 border-red-200',
        textClass: 'text-red-900',
        badgeClass: 'bg-red-500 text-white',
        icon: AlertTriangle
      },
      medium: {
        bgClass: 'bg-orange-50 border-orange-200',
        textClass: 'text-orange-900',
        badgeClass: 'bg-orange-500 text-white',
        icon: Info
      },
      low: {
        bgClass: 'bg-yellow-50 border-yellow-200',
        textClass: 'text-yellow-900',
        badgeClass: 'bg-yellow-500 text-white',
        icon: Info
      },
      none: {
        bgClass: 'bg-green-50 border-green-200',
        textClass: 'text-green-900',
        badgeClass: 'bg-green-500 text-white',
        icon: CheckCircle
      }
    };
    return configs[severity] || configs.none;
  };

  const getTypeLabel = (type) => {
    const labels = {
      correlation: 'Pattern Detected',
      positive_correlation: 'Positive Pattern',
      observation: 'Observation',
      achievement: 'Achievement',
      encouragement: 'Progress',
      tip: 'Smart Tip',
      personalized: 'Personalized for You'
    };
    return labels[type] || 'Insight';
  };

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center text-indigo-900">
          <Zap className="h-5 w-5 mr-2" />
          Personalized Medication Intelligence
        </CardTitle>
        <p className="text-xs text-indigo-700 mt-1">
          AI-powered insights based on your sleep, stress, and adherence patterns
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {insights.map((insight, index) => {
            const config = getSeverityConfig(insight.severity);
            const Icon = config.icon;

            return (
              <Alert key={index} className={`${config.bgClass} border-2`}>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{insight.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className={`font-semibold text-sm ${config.textClass}`}>
                        {insight.title}
                      </h4>
                      <Badge className={`${config.badgeClass} text-xs`}>
                        {getTypeLabel(insight.type)}
                      </Badge>
                    </div>
                    <AlertDescription className={`text-sm ${config.textClass}`}>
                      {insight.message}
                    </AlertDescription>
                    {insight.action && (
                      <div className="mt-2 flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span className="text-xs font-semibold">
                          Action: {insight.action}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicationIntelligencePanel;
